import React from 'react';
import { Label, NumberField } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { IMPORT_CURRENCY } from '../manualImportMeta';

type LabeledNumberFieldProps = {
    label: string;
    value: string;
    onChange: (value: string) => void;
    slot: string;
    currency?: boolean;
    percent?: boolean;
    minValue?: number;
    readOnly?: boolean;
};

/** The unit style keeps the entered number as-is (50 → "50%"), unlike
 *  Intl's percent style which scales by 100 (50 → "5,000%"). */
const PERCENT_FORMAT: Intl.NumberFormatOptions = {
    style: 'unit',
    unit: 'percent',
};

/**
 * BOS-162: `narrowSymbol` gives ₾, the sign every other amount in the app uses.
 * The default renders the ISO code, which made this input the one figure on the
 * page written as `GEL 100.00` while the row it feeds read `₾ 100.00`.
 */
const CURRENCY_FORMAT: Intl.NumberFormatOptions = {
    currency: IMPORT_CURRENCY,
    currencyDisplay: 'narrowSymbol',
    style: 'currency',
};

/** Number input with steppers; `currency` formats the value as GEL, `percent`
 *  suffixes it with %, `readOnly` shows a derived value that can't be edited. */
export const LabeledNumberField: React.FC<LabeledNumberFieldProps> = ({
    label,
    value,
    onChange,
    slot,
    currency,
    percent,
    minValue = 0,
    readOnly,
}) => (
    <NumberField
        formatOptions={
            currency ? CURRENCY_FORMAT : percent ? PERCENT_FORMAT : undefined
        }
        isReadOnly={readOnly}
        minValue={minValue}
        onChange={(next) =>
            onChange(
                next === undefined || Number.isNaN(next) ? '' : String(next),
            )
        }
        value={value === '' ? undefined : Number(value)}
        variant="secondary"
        {...getNodeTestIdProp(`manual-import-number-${slot}`)}
    >
        <Label
            id={`manual-import-number-label-${slot}`}
            {...getNodeTestIdProp(`manual-import-number-label-${slot}`)}
        >
            {label}
        </Label>
        <NumberField.Group
            {...getNodeTestIdProp(`manual-import-number-group-${slot}`)}
        >
            <NumberField.DecrementButton
                {...getNodeTestIdProp(`manual-import-number-decrement-${slot}`)}
            />
            <NumberField.Input
                {...getNodeTestIdProp(`manual-import-number-control-${slot}`)}
            />
            <NumberField.IncrementButton
                {...getNodeTestIdProp(`manual-import-number-increment-${slot}`)}
            />
        </NumberField.Group>
    </NumberField>
);
